<template>
  <div class="min-h-screen bg-black text-white p-4 sm:p-6 md:p-8">
    <div class="max-w-5xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8 border-b-2 border-yellow-500 pb-6">
        <h1 class="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-yellow-500">🥋 COMBATES POR COMPETIDOR 🥋</h1>
        <p class="text-sm sm:text-base md:text-lg text-yellow-400" v-if="torneoNombre">{{ torneoNombre }}</p>
      </div>

      <!-- Selectores -->
      <div class="mb-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-400 uppercase tracking-wide">Torneo:</label>
          <select
            v-model="torneoSeleccionado"
            @change="cambiarTorneo"
            class="bg-gray-900 border border-yellow-500 text-white rounded-lg px-4 py-2 min-w-[220px]"
          >
            <option v-for="t in torneos" :key="t.id" :value="t.id">{{ t.nombre }}</option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-400 uppercase tracking-wide">Categoría:</label>
          <select
            v-model="categoriaSeleccionada"
            @change="cambiarCategoria"
            class="bg-gray-900 border border-yellow-500 text-white rounded-lg px-4 py-2 min-w-[220px]"
            :disabled="!categorias.length"
          >
            <option v-for="c in categorias" :key="c.categoria_id" :value="c.categoria_id">{{ c.nombre }}</option>
          </select>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="animate-spin text-4xl mb-4">⏳</div>
        <p class="text-gray-400 text-lg">Cargando...</p>
      </div>

      <!-- Sin torneo -->
      <div v-else-if="!torneoSeleccionado" class="text-center py-16">
        <p class="text-xl text-gray-400">No hay torneos disponibles</p>
      </div>

      <!-- Sin categoría -->
      <div v-else-if="!categoriaSeleccionada" class="text-center py-16">
        <p class="text-xl text-gray-400">Este torneo no tiene categorías asignadas</p>
      </div>

      <!-- Tabla -->
      <div v-else class="bg-gray-900 rounded-lg border border-yellow-500 border-opacity-30 overflow-hidden">
        <div v-if="!competidores.length" class="text-center py-12">
          <p class="text-gray-400">Esta categoría todavía no tiene combates registrados</p>
        </div>

        <table v-else class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-800 text-yellow-500 uppercase text-xs sm:text-sm">
              <th class="px-3 sm:px-4 py-3">Puesto</th>
              <th class="px-3 sm:px-4 py-3">Competidor</th>
              <th class="px-3 sm:px-4 py-3 hidden sm:table-cell">Escuela</th>
              <th class="px-3 sm:px-4 py-3 hidden md:table-cell">Regional</th>
              <th class="px-3 sm:px-4 py-3 text-center">Combates</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(fila, idx) in competidores"
              :key="fila.competidor.id"
              class="border-t border-gray-800"
              :class="fila.posicion ? 'bg-yellow-500 bg-opacity-10' : (idx % 2 === 0 ? 'bg-gray-900' : 'bg-gray-800 bg-opacity-40')"
            >
              <td class="px-3 sm:px-4 py-3 font-bold text-lg">
                <span v-if="fila.posicion === 1">🥇</span>
                <span v-else-if="fila.posicion === 2">🥈</span>
                <span v-else-if="fila.posicion === 3">🥉</span>
                <span v-else-if="fila.posicion === 4" class="text-sm text-gray-300">4°</span>
                <span v-else class="text-sm text-gray-600">—</span>
              </td>
              <td class="px-3 sm:px-4 py-3">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full overflow-hidden bg-white flex items-center justify-center flex-shrink-0 border border-gray-700">
                    <img
                      v-if="getFotoUrl(fila.competidor.foto_url)"
                      :src="getFotoUrl(fila.competidor.foto_url)"
                      class="w-full h-full object-cover"
                      @error="$event.target.style.display = 'none'"
                    />
                    <span v-else class="text-lg">🥋</span>
                  </div>
                  <div>
                    <p class="font-bold text-yellow-400 uppercase leading-tight">
                      {{ fila.competidor.apelido || fila.competidor.nombre_completo }}
                    </p>
                    <p v-if="fila.competidor.apelido" class="text-xs text-gray-400 leading-tight">
                      {{ fila.competidor.nombre_completo }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="px-3 sm:px-4 py-3 hidden sm:table-cell text-sm text-gray-300">{{ fila.competidor.escuela_nombre || '—' }}</td>
              <td class="px-3 sm:px-4 py-3 hidden md:table-cell text-sm text-gray-300">{{ fila.competidor.regional_nombre || '—' }}</td>
              <td class="px-3 sm:px-4 py-3 text-center">
                <span class="inline-block bg-yellow-600 text-black font-bold rounded-full w-8 h-8 leading-8 text-sm">
                  {{ fila.combates }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import { getFotoUrl } from '../../config'

const route = useRoute()
const router = useRouter()
const api = useAPI()

const torneos = ref([])
const torneoSeleccionado = ref(null)
const torneoNombre = ref('')
const categorias = ref([])
const categoriaSeleccionada = ref(null)
const competidores = ref([])
const loading = ref(true)

const cargarCategoriasDelTorneo = async (torneoId) => {
  const torneo = await api.getTorneo(torneoId)
  torneoNombre.value = torneo.nombre
  categorias.value = torneo.categorias || []
}

const cargarCombates = async (torneoId, categoriaId) => {
  if (!categoriaId) {
    competidores.value = []
    return
  }
  const data = await api.getCombatesPorCompetidor(torneoId, categoriaId)
  competidores.value = data.competidores || []
}

const cambiarTorneo = async () => {
  if (!torneoSeleccionado.value) return
  loading.value = true
  try {
    router.replace({ name: 'CombatesPorCompetidor', params: { torneoId: torneoSeleccionado.value } })
    await cargarCategoriasDelTorneo(torneoSeleccionado.value)
    categoriaSeleccionada.value = categorias.value[0]?.categoria_id ?? null
    await cargarCombates(torneoSeleccionado.value, categoriaSeleccionada.value)
  } catch (err) {
    console.error('Error cambiando de torneo:', err)
  } finally {
    loading.value = false
  }
}

const cambiarCategoria = async () => {
  loading.value = true
  try {
    await cargarCombates(torneoSeleccionado.value, categoriaSeleccionada.value)
  } catch (err) {
    console.error('Error cambiando de categoría:', err)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  loading.value = true
  try {
    torneos.value = await api.getTorneos()

    const idDeRuta = route.params.torneoId ? parseInt(route.params.torneoId) : null
    const torneoInicial = idDeRuta && torneos.value.some(t => t.id === idDeRuta)
      ? idDeRuta
      : (torneos.value[0]?.id ?? null)

    torneoSeleccionado.value = torneoInicial

    if (torneoInicial) {
      await cargarCategoriasDelTorneo(torneoInicial)
      categoriaSeleccionada.value = categorias.value[0]?.categoria_id ?? null
      await cargarCombates(torneoInicial, categoriaSeleccionada.value)
    }
  } catch (err) {
    console.error('Error cargando datos:', err)
  } finally {
    loading.value = false
  }
})
</script>
